import { useEffect, useState } from 'react';
import { signInWithCustomToken, signOut, User } from 'firebase/auth';
import { auth } from '@Services/firebase/firestore';
import { useLazyGetFirebaseTokenQuery } from './apiSlice';

export function useFirebaseAuth() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [trigger, { data, isLoading: isFetchingToken }] = useLazyGetFirebaseTokenQuery();

    useEffect(() => {
        let mounted = true;

        const initAuth = async () => {
            // 1. Check if already authenticated
            if (auth.currentUser) {
                setUser(auth.currentUser);
                setLoading(false);
                return;
            }

            // 2. Fetch custom token from backend
            try {
                // Determine if we need to fetch a token.
                // We typically assume the user is logged into the main app (valid JWT)
                // We use the lazy query to fetch just once
                const response = await trigger().unwrap();

                if (response?.data?.token) {
                    const userCredential = await signInWithCustomToken(auth, response?.data?.token);
                    if (mounted) {
                        setUser(userCredential.user);
                    }
                }
            } catch (err: any) {
                console.error("Firebase auth failed", err);
                if (mounted) setError(err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        initAuth();

        // 3. Listen for auth state changes
        const unsubscribe = auth.onAuthStateChanged((u) => {
            if (mounted) setUser(u);
        });

        // 4. Cleanup on unmount (Sign out)
        return () => {
            mounted = false;
            unsubscribe();
            // User requested explicit signout when leaving inbox page
            signOut(auth).catch(console.error);
        };
    }, []);

    return { user, loading: loading || isFetchingToken, error };
}
